import { useEffect, useRef } from 'react';

const smooth = (t: number) => t * t * (3 - 2 * t);
const clamp = (v: number) => Math.max(0, Math.min(1, v));

/**
 * Tone field (0..1) in a normalised 1200x800 space, evolving slowly with time t (s).
 * Same composition as the approved texture (diagonal band + hollow ring + corner wash),
 * but with wide soft falloffs, a capped peak and low-frequency drift so it never forms solid masses.
 */
const tone = (gx: number, gy: number, t: number) => {
  // incommensurate speeds → evolves without an obvious loop
  const curveY = 470 + 150 * Math.sin(gx / 230 + 0.6 + t * 0.13) - gx * 0.18 + 40 * Math.sin(t * 0.17);
  const band = Math.exp(-(((gy - curveY) / 105) ** 2));
  const d = Math.hypot(gx - 600 - 60 * Math.sin(t * 0.11), gy - 400 - 40 * Math.cos(t * 0.083));
  const ring = Math.exp(-(((d - (330 + 30 * Math.sin(t * 0.19))) / 60) ** 2)) * 0.62;
  // soft secondary stream crossing the centre so the field flows continuously behind the orbit
  const crossY = 380 + 90 * Math.sin(gx / 260 - t * 0.15) + (gx - 600) * 0.32;
  const cross = Math.exp(-(((gy - crossY) / 85) ** 2)) * 0.55;
  const hollow = 1;
  const wash = clamp(1 - Math.hypot(gx - 160, gy - 120) / 560) * 0.14;
  const drift = 0.16 * Math.sin(gx / 97 + t * 0.29) * Math.cos(gy / 83 - t * 0.23)
    + 0.08 * Math.sin((gx + gy) / 61 + t * 0.37);
  const raw = Math.max(band, ring, cross) * hollow * 0.8 + wash + drift;
  // lift the floor so light regions open up fully; dense areas become accents
  return clamp((raw - 0.2) / 0.8);
};

/** invisible smoke puff: a soft heat source that drifts, expands and dissolves */
interface Puff { x: number; y: number; vx: number; vy: number; t: number; r0: number; life: number; seed: number }
const MAX_PUFFS = 70;
interface Wave { x: number; y: number; t0: number }

/** One fixed visual world behind the whole page, revealed through transparent windows. Single canvas, single loop. */
export default function VisualWorld() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const orbitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    let w = 0; let h = 0; let step = 8; let rowH = 7; let cols = 0; let rows = 0;
    let heat = new Float32Array(0);
    const size = () => {
      w = window.innerWidth; h = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      step = w < 768 ? 6.5 : 8; rowH = step * 0.87;
      cols = Math.ceil(w / step) + 1; rows = Math.ceil(h / rowH) + 1;
      heat = new Float32Array(cols * rows);
    };
    size();

    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const trail: Puff[] = [];
    const waves: Wave[] = [];
    let lastWave = { x: -1e4, y: -1e4 };
    const ptr = { x: w / 2, y: h / 2 };
    const tilt = { x: w / 2, y: h / 2, s: window.scrollY };
    const t0 = performance.now();

    const draw = (now: number) => {
      const t = (now - t0) / 1000;
      // age out pointer data
      for (let k = trail.length - 1; k >= 0; k -= 1) if (now - trail[k].t > trail[k].life) trail.splice(k, 1);
      for (let k = waves.length - 1; k >= 0; k -= 1) if (now - waves[k].t0 > 1500) waves.splice(k, 1);

      // smoke heat: drifting, expanding puffs accumulated additively into the dot grid (no shapes drawn)
      heat.fill(0);
      for (let i = 0; i < trail.length; i += 1) {
        const p = trail[i];
        const age = (now - p.t) / p.life;
        const life = smooth(clamp(1 - age)) * smooth(clamp(age / 0.12));
        const drag = 1 - Math.exp(-age * 2.2);
        const pxp = p.x + p.vx * drag; const pyp = p.y + p.vy * drag;
        const rad = p.r0 * (1 + age * 0.9); // mist expands as it disperses
        const rr = Math.ceil(rad / rowH) + 1;
        const r0 = Math.max(0, Math.floor(pyp / rowH) - rr);
        const r1 = Math.min(rows - 1, Math.floor(pyp / rowH) + rr);
        for (let row = r0; row <= r1; row += 1) {
          const by = step / 2 + row * rowH;
          const off = row % 2 ? step / 2 : 0;
          const c0 = Math.max(0, Math.floor((pxp - rad - off) / step) - 1);
          const c1 = Math.min(cols - 1, Math.ceil((pxp + rad - off) / step) + 1);
          for (let c = c0; c <= c1; c += 1) {
            const qx = step / 2 + off + c * step;
            const dd = Math.hypot(qx - pxp, by - pyp) / rad;
            if (dd < 1) {
              // wispy modulation breaks the circular outline into mist
              const wisp = 0.62 + 0.38 * Math.sin(qx / 13 + p.seed + age * 3) * Math.cos(by / 11 - p.seed * 1.7 + age * 2);
              const idx = row * cols + c;
              heat[idx] = Math.min(1, heat[idx] + (1 - dd * dd) ** 2 * life * wisp * 0.32);
            }
          }
        }
      }

      const scale = Math.max(1200 / w, 800 / h);
      const ox = (1200 - w * scale) / 2;
      const oy = (800 - h * scale) / 2;
      const rMax = step * 0.34; // caps coverage ~40% → dots never merge
      ctx.clearRect(0, 0, w, h);
      const ink = new Path2D();
      const tint = new Path2D();
      const soft = new Path2D();
      for (let row = 0; row < rows; row += 1) {
        const by = step / 2 + row * rowH;
        const off = row % 2 ? step / 2 : 0;
        for (let c = 0; c < cols; c += 1) {
          const bx = step / 2 + off + c * step;
          const gx = ox + bx * scale; const gy = oy + by * scale;
          const tv = tone(gx, gy, t);
          let r = Math.sqrt(tv) * rMax;
          let px = bx; let py = by;
          for (let k = 0; k < waves.length; k += 1) {
            const wv = waves[k];
            const age = (now - wv.t0) / 1000;
            const dx = bx - wv.x; const dy = by - wv.y;
            const d = Math.hypot(dx, dy) || 1;
            const wd = d - age * 170;
            if (wd > -40 && wd < 40) {
              const push = Math.sin(wd / 9) * Math.exp(-((wd / 22) ** 2)) * (1 - age / 1.5) * Math.max(0, 1 - d / 360) * 4;
              px += (dx / d) * push; py += (dy / d) * push;
              r += Math.abs(push) * 0.1;
            }
          }
          const hv = heat[row * cols + c];
          if (hv > 0.02) r = Math.max(r, rMax * 0.85 * hv) + hv * 0.5;
          if (r < 0.35) continue;
          r = Math.min(r, step * 0.42);
          // three tones → diffused edge instead of a hard line
          let path = ink;
          if (hv > 0.5) path = tint;
          else if (hv > 0.14) path = soft;
          path.moveTo(px + r, py);
          path.arc(px, py, r, 0, Math.PI * 2);
        }
      }
      ctx.fillStyle = '#686CC4';
      ctx.fill(ink);
      ctx.fillStyle = '#A5A7E6';
      ctx.fill(soft);
      ctx.fillStyle = '#44479A';
      ctx.fill(tint);
    };

    /** inertial wake head: follows the cursor with lag, stamping the path each frame */
    const lag = { x: ptr.x, y: ptr.y, on: false };
    let lastEmit = 0;
    const stepTrail = (now: number) => {
      if (!lag.on) return;
      const nx = lag.x + (ptr.x - lag.x) * 0.3;
      const ny = lag.y + (ptr.y - lag.y) * 0.3;
      const dist = Math.hypot(nx - lag.x, ny - lag.y);
      if (dist > 0.8 && now - lastEmit > 28) {
        lastEmit = now;
        const sp = Math.min(1, dist / 30); // speed → slightly longer, still compact wake
        const dx = (nx - lag.x) / dist; const dy = (ny - lag.y) / dist;
        const n = 1 + Math.round(sp * 2);
        for (let i = 0; i < n; i += 1) {
          const j = (Math.random() - 0.5) * 44;
          trail.push({
            x: nx - dy * j + (Math.random() - 0.5) * 22,
            y: ny + dx * j + (Math.random() - 0.5) * 22,
            // drift backward along the path + random curl → clustered smoky wake
            vx: -dx * (10 + sp * 26) + (Math.random() - 0.5) * 30,
            vy: -dy * (10 + sp * 26) + (Math.random() - 0.5) * 30 - 6,
            t: now,
            r0: 58 + Math.random() * 30,
            life: 520 + sp * 260 + Math.random() * 180,
            seed: Math.random() * 6.28,
          });
        }
        if (trail.length > MAX_PUFFS) trail.splice(0, trail.length - MAX_PUFFS);
      }
      lag.x = nx; lag.y = ny;
    };

    const tiltOrbit = () => {
      tilt.x += (ptr.x - tilt.x) * 0.08;
      tilt.y += (ptr.y - tilt.y) * 0.08;
      tilt.s += (window.scrollY - tilt.s) * 0.1;
      if (orbitRef.current) {
        const nx = tilt.x / w - 0.5;
        const ny = tilt.y / h - 0.5;
        const sc = Math.sin(tilt.s * 0.0012) * 6;
        orbitRef.current.style.transform = `translate(-50%, -50%) rotateX(${(-18 + sc + ny * -10).toFixed(2)}deg) rotateY(${(nx * 12).toFixed(2)}deg)`;
      }
    };

    let raf = 0;
    let last = 0;
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      tiltOrbit();
      stepTrail(now);
      const active = trail.length > 0 || waves.length > 0;
      // texture evolves slowly → 20fps idle is visually smooth; 40fps while the pointer is interacting
      if (now - last < (active ? 24 : 50)) return;
      last = now;
      draw(now);
    };

    let rt = 0;
    const onResize = () => {
      clearTimeout(rt);
      rt = window.setTimeout(() => { size(); draw(performance.now()); }, 140);
    };
    const onMove = (e: PointerEvent) => {
      ptr.x = e.clientX; ptr.y = e.clientY;
      if (!lag.on) { lag.x = e.clientX; lag.y = e.clientY; lag.on = true; }
      const now = performance.now();
      if (Math.hypot(e.clientX - lastWave.x, e.clientY - lastWave.y) > 90 && waves.length < 3) {
        waves.push({ x: e.clientX, y: e.clientY, t0: now });
        lastWave = { x: e.clientX, y: e.clientY };
      }
    };
    const onVis = () => {
      if (document.hidden) { cancelAnimationFrame(raf); raf = 0; } else if (!raf && !reduce) raf = requestAnimationFrame(tick);
    };

    const onLeave = () => { lag.on = false; };
    document.documentElement.addEventListener('mouseleave', onLeave);
    window.addEventListener('resize', onResize);
    draw(performance.now());
    if (!reduce) {
      raf = requestAnimationFrame(tick);
      document.addEventListener('visibilitychange', onVis);
      if (fine) window.addEventListener('pointermove', onMove, { passive: true });
    }
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(rt);
      window.removeEventListener('resize', onResize);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <div className="vw" aria-hidden="true">
      <canvas ref={canvasRef} className="vw-dots" />
      <div className="vw-orbit-stage">
        <div ref={orbitRef} className="vw-orbit">
          <div className="vw-orbit-spin">
            {[
              { t: 'rotateX(80deg)', c: 'accent', bw: '3px', sat: true },
              { t: 'rotateZ(38deg) rotateX(68deg)', c: '', bw: '1.6px', sat: false },
              { t: 'rotateZ(-46deg) rotateX(64deg)', c: 'inner', bw: '1.4px', sat: false },
            ].map((r) => (
              <div key={r.t} className={`orb-ring ${r.c}`} style={{ transform: r.t }}>
                {[0, 1, 2, 3].map((z) => (
                  <span key={z} className="orb-band" style={{ transform: `translateZ(${z * 1.5}px)`, ['--bw' as string]: r.bw, opacity: 1 - z * 0.18 }} />
                ))}
                {r.sat && <span className="orb-sat" />}
              </div>
            ))}
          </div>
          <span className="orb-shadow" />
          <span className="orb-core" />
        </div>
      </div>
    </div>
  );
}